import type { AppendMessage, CreateAppendMessage } from '@assistant-ui/react'
import { format } from 'date-fns'

import type {
  WithFocusFiles,
  WithSelectedConnectorSources,
  WithSelectedMcpServers,
  WithViewportContext,
  WithWebSearchAnswerTool,
} from '@/types/chat-v2/sessions'

export type ChatExportExtension = 'csv' | 'docx' | 'html' | 'pdf'

export const CHAT_EXPORT_BRANCH_METADATA_KEY = 'chat_export_branch'

export const isChatExportBranchMessage = (message: {
  metadata?: AppendMessage['metadata']
}): boolean => {
  const custom = message.metadata?.custom
  return (
    typeof custom === 'object' &&
    custom !== null &&
    !Array.isArray(custom) &&
    (custom as Record<string, unknown>)[CHAT_EXPORT_BRANCH_METADATA_KEY] ===
      true
  )
}

/**
 * Strips draft-derived send params from a synthetic message-scoped action.
 * Exports and hidden continuations act on existing chat history; the live
 * composer draft must never ride along with them.
 *
 * This helper owns the send-param strip list: focus files, viewport context,
 * web-search method, and composer integrations. Other draft state has its own
 * owners: document selection in `usePursuitDocumentCreateMessageParams`, and
 * composer attachments/text in `ChatRuntimeProvider`.
 */
type ComposerDraftParams = WithFocusFiles &
  WithSelectedConnectorSources &
  WithSelectedMcpServers &
  WithViewportContext &
  WithWebSearchAnswerTool

type WithoutComposerDraftParams<T extends ComposerDraftParams> = Omit<
  T,
  keyof ComposerDraftParams
> &
  Required<WithFocusFiles> &
  Required<WithSelectedConnectorSources> &
  Required<WithSelectedMcpServers> &
  Required<WithWebSearchAnswerTool> & { viewport_context: null }

export const withoutComposerDraftParams = <T extends ComposerDraftParams>(
  params: T,
): WithoutComposerDraftParams<T> => ({
  ...params,
  focus_opportunity_file_ids: [],
  focus_organization_file_ids: [],
  focus_pursuit_file_ids: [],
  focus_user_file_ids: [],
  viewport_context: null,
  selected_connector_sources: [],
  selected_mcp_server_ids: [],
  // Sticky Deep Research / forced web-search picks are composer state. Keep
  // FedRAMP's disabled setting authoritative.
  search_method:
    params.search_method === 'disabled' ? 'disabled' : 'allow-web-search',
})

type ChatExportBranchMessage = Exclude<CreateAppendMessage, string>

export const createChatExportBranchMessage = (
  parentId: string,
  prompt: string,
): ChatExportBranchMessage => ({
  parentId,
  role: 'user',
  content: [{ type: 'text', text: prompt }],
  metadata: {
    custom: { [CHAT_EXPORT_BRANCH_METADATA_KEY]: true },
  },
})

const csvFormulaPrefixPattern = /^[=+\-@]/

const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100)
}

const getReadableDate = (): string => {
  return format(new Date(), 'yyyy-MM-dd')
}

export const generateChatExportFilename = (
  title: string | null | undefined,
  extension: ChatExportExtension,
): string => {
  if (title && title.trim()) {
    const sanitizedTitle = sanitizeFilename(title.trim())
    if (sanitizedTitle) {
      return `sweetspot-${sanitizedTitle}.${extension}`
    }
  }

  return `sweetspot-chat-export-${getReadableDate()}.${extension}`
}

const normalizeCsvCellValue = (value: string): string => {
  return value
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim()
}

const escapeCsvCell = (value: string): string => {
  const normalizedValue = normalizeCsvCellValue(value)
  const sanitizedValue = csvFormulaPrefixPattern.test(normalizedValue)
    ? `'${normalizedValue}`
    : normalizedValue

  if (!/[",\n]/.test(sanitizedValue)) {
    return sanitizedValue
  }

  return `"${sanitizedValue.replace(/"/g, '""')}"`
}

export const rowsToCsv = (rows: string[][]): string => {
  return rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n')
}

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  // Defer revocation: revoking synchronously can cancel the download in
  // Safari and some other browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const downloadTextFile = (
  content: string,
  filename: string,
  mimeType: string,
): void => {
  downloadBlob(new Blob([content], { type: mimeType }), filename)
}
